// Fetches both Data API and Pay API statuses and returns a combined result
import { normalizeStatus } from '../utils/statusUtils';

const TIMEOUT_MS = 10000; // 10 second timeout

/**
 * Fetches a health endpoint with timeout and error handling
 */
async function fetchWithTimeout(url, options = {}) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
      headers: {
        'Accept': 'application/json',
        ...options.headers
      }
    });
    clearTimeout(timeoutId);
    return { response, error: null };
  } catch (error) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      return { response: null, error: new Error('Request timeout') };
    }
    // Enhance error message for better debugging
    let enhancedError = error;
    if (error.message && (error.message.includes('Failed to fetch') || error.message.includes('NetworkError'))) {
      enhancedError = new Error(`Network error: ${error.message}. This may be due to CORS policy, network connectivity, or the service being unavailable.`);
      enhancedError.name = error.name;
      enhancedError.originalError = error;
    }
    return { response: null, error: enhancedError };
  }
}

/**
 * Fetches health status for a single service
 */
async function fetchServiceHealth(url, serviceName) {
  const { response, error } = await fetchWithTimeout(url);
  const lastChecked = new Date();

  if (error || !response) {
    // Check if it's a CORS error specifically
    const errorMessage = error?.message || 'Network error';
    const errorString = String(errorMessage).toLowerCase();
    
    // Check if the error message mentions a 403 status code - if so, it's an auth error, not CORS
    const has403Status = errorString.includes('403') || 
                        errorString.includes('status code: 403') ||
                        errorString.includes('código de estado: 403');
    
    if (has403Status) {
      // Even though CORS blocked reading the response, the server returned 403
      return {
        name: serviceName,
        status: 'Unknown',
        lastChecked,
        httpStatus: 403,
        error: 'Access forbidden (403) - CORS also prevents reading response',
        raw: null,
        requiresAuth: true
      };
    }
    
    const isCorsError = errorMessage.includes('CORS') || 
                       errorMessage.includes('Failed to fetch') ||
                       errorMessage.includes('NetworkError') ||
                       (error?.name === 'TypeError' && errorMessage.includes('fetch'));
    
    return {
      name: serviceName,
      status: isCorsError ? 'Unknown' : 'Major Outage',
      lastChecked,
      error: errorMessage,
      raw: null,
      corsBlocked: isCorsError
    };
  }

  // Check response status first - handle non-OK responses before trying to read body
  // Wrap in try-catch in case CORS blocks access to response properties
  let responseStatus;
  try {
    responseStatus = response.status;
  } catch (e) {
    // If we can't read status, it might be a CORS issue
    return {
      name: serviceName,
      status: 'Unknown',
      lastChecked,
      error: 'Cannot read response status - may be CORS blocked',
      raw: null,
      corsBlocked: true
    };
  }

  if (!response.ok) {
    // Handle authentication/authorization errors - these are NOT CORS errors
    if (responseStatus === 401 || responseStatus === 403) {
      return {
        name: serviceName,
        status: 'Unknown',
        lastChecked,
        httpStatus: responseStatus,
        error: responseStatus === 401 ? 'Authentication required' : 'Access forbidden (403)',
        raw: null,
        requiresAuth: true
      };
    }
    return {
      name: serviceName,
      status: responseStatus >= 500 ? 'Major Outage' : 'Degraded Performance',
      lastChecked,
      httpStatus: responseStatus,
      raw: null
    };
  }

  try {
    // Only try to read headers/body if response is OK (200-299)
    // Double-check status before trying to read headers (in case response.ok was wrong)
    if (responseStatus === 401 || responseStatus === 403) {
      return {
        name: serviceName,
        status: 'Unknown',
        lastChecked,
        httpStatus: responseStatus,
        error: responseStatus === 401 ? 'Authentication required' : 'Access forbidden (403)',
        raw: null,
        requiresAuth: true
      };
    }
    
    // Check content type before attempting to parse
    const contentType = response.headers.get('content-type') || '';
    const isJson = contentType.includes('application/json');
    
    let json;
    if (isJson) {
      json = await response.json();
    } else {
      // Try to read as text to see what we got
      const text = await response.text();
      // Try to parse as JSON anyway (some servers don't set content-type correctly)
      try {
        json = JSON.parse(text);
      } catch {
        // Not JSON, return operational status
        return {
          name: serviceName,
          status: 'Operational',
          lastChecked,
          raw: { note: 'Non-JSON response', contentType, preview: text.substring(0, 100) }
        };
      }
    }
    
    // Handle different response formats
    let status = 'Unknown';
    
    if (json.status !== undefined) {
      status = json.status === 200 || json.status === 'healthy' ? 'Operational' : String(json.status);
    } else if (json.health !== undefined) {
      status = json.health === 'healthy' ? 'Operational' : json.health;
    } else if (json.running !== undefined) {
      status = json.running === true ? 'Operational' : 'Major Outage';
    } else {
      // If response is valid JSON but doesn't match expected format, default to Operational
      // Default to Operational if we got a 200 response
      status = 'Operational';
    }

    return {
      name: serviceName,
      status: normalizeStatus(status),
      lastChecked,
      raw: json
    };
  } catch (parseError) {
    // Check response status first - if it's 401/403, it's an auth error, NOT CORS
    const errorMessage = parseError.message || String(parseError);
    
    // Try to get response status if we don't have it yet
    let statusToCheck = responseStatus;
    if (statusToCheck === undefined && response) {
      try {
        statusToCheck = response.status;
      } catch (e) {
        // Can't read status, might be CORS
      }
    }
    
    // If we have a 401 or 403 status, it's an authorization issue, not CORS
    if (statusToCheck === 401 || statusToCheck === 403) {
      return {
        name: serviceName,
        status: 'Unknown',
        lastChecked,
        httpStatus: statusToCheck,
        error: statusToCheck === 401 ? 'Authentication required' : 'Access forbidden (403)',
        raw: null,
        requiresAuth: true
      };
    }
    
    // Check if it's likely a CORS issue (can't read response even though request succeeded)
    const isCorsReadError = errorMessage.includes('CORS') ||
      errorMessage.includes('network') ||
      errorMessage.includes('Failed to execute') ||
      errorMessage.includes('TypeError');
    
    if (isCorsReadError && responseStatus >= 200 && responseStatus < 300) {
      // Request succeeded but can't read response - classic CORS issue
      return {
        name: serviceName,
        status: 'Unknown',
        lastChecked,
        error: 'CORS policy prevents reading response (request succeeded but response is blocked)',
        raw: null,
        corsBlocked: true
      };
    }
    
    // If response is OK but not JSON, consider it operational
    return {
      name: serviceName,
      status: 'Operational',
      lastChecked,
      error: `Parse error: ${errorMessage}`,
      raw: { note: 'Non-JSON response' }
    };
  }
}

/**
 * Fetches CXP services status
 */
export async function fetchCxpServicesStatus() {
  const endpoints = [
    { url: 'https://cxp-testqa01-api.azurewebsites.net/health', name: 'Data API' },
    { url: 'https://cxp-testqa01-pay.azurewebsites.net/health', name: 'Pay API' },
    { url: 'https://cxp-testqa01-processor.azurewebsites.net/', name: 'Processor API' },
    { url: 'https://cxp-testqa03-api.azurewebsites.net', name: 'test-qa03 api' }
  ];

  try {
    const results = await Promise.allSettled(
      endpoints.map(endpoint => fetchServiceHealth(endpoint.url, endpoint.name))
    );

    return results.map((result, index) => {
      if (result.status === 'fulfilled') {
        return result.value;
      }
      return {
        name: endpoints[index].name,
        status: 'Major Outage',
        lastChecked: new Date(),
        error: result.reason?.message || 'Unknown error',
        raw: null
      };
    });
  } catch (error) {
    // Fallback if Promise.allSettled fails (shouldn't happen, but just in case)
    return endpoints.map(endpoint => ({
      name: endpoint.name,
      status: 'Major Outage',
      lastChecked: new Date(),
      error: error.message || 'Failed to fetch status',
      raw: null
    }));
  }
}
